import type { DataSyncTemplate } from './validations/types';

import {
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDataSyncJob } from './queryKeys';
import { parseDataSyncResponse } from './responseShapes';
import { dataSyncTemplateListSchema } from './validations';

/**
 * Fetches the per-system templates a sync job can be created from.
 */
export const fetchDataSyncTemplates = async (
  options?: KyRequestOptions,
): Promise<DataSyncTemplate[]> => {
  const response = await vestigasApi
    .get(ENDPOINT.DATA_SYNC.GET_TEMPLATES(), options)
    .json<unknown>();

  return parseDataSyncResponse(
    dataSyncTemplateListSchema,
    response,
    'template list',
  ).data;
};

/**
 * Generates the query options for the data sync templates query, e.g. for
 * loaders.
 *
 * The templates are hardcoded in the backend and only change when it deploys,
 * so they are cached for an hour rather than refetched per view.
 *
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
// fallow-ignore-next-line unused-export -- queryOptions are exported for loader reuse per the tanstack-query-hooks skill
export const getDataSyncTemplatesQueryOptions = <TData = DataSyncTemplate[]>(
  options?: Omit<
    UseQueryOptions<DataSyncTemplate[], Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    // The consumers degrade inline (the picker shows an alert, a job card
    // just leaves its system chip off), so a failed fetch must neither throw
    // into the route error boundary nor fire the global error handling next
    // to the inline alert. Both suppressions are needed: `throwOnError` only
    // covers the hook, `suppressError` the QueryCache handler.
    meta: { suppressError: true },
    queryFn: async ({ signal }) => fetchDataSyncTemplates({ signal }),
    queryKey: queryKeysDataSyncJob.getTemplates(),
    staleTime: ms('1h'),
    throwOnError: false,
    ...options,
  });

/**
 * React Query hook for the data sync templates.
 *
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 */
export const useQueryDataSyncTemplates = <TData = DataSyncTemplate[]>(
  options?: Omit<
    UseQueryOptions<DataSyncTemplate[], Error, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getDataSyncTemplatesQueryOptions<TData>(options));
